import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getCasinoOpenBets } from '../../services/casinoService'
import LoaderOverlay from '../LoaderOverlay'
import SimpleBarBox from '../SimpleBarBox'
import useStickySidebar from '../../hooks/useStickySidebar'
import { formatStake, formatBetAmount, formatRouletteBetName } from '../../utils/casinoUtils'

const formatPlaceDate = (val) => {
  if (!val) return ''
  const d = new Date(val)
  if (isNaN(d)) return String(val)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}:${ss}`
}

// Admin reference right sidebar — "My Bets" card (UserName/Rate/Amount of the
// downline bets on the current round) + mobile floating Bets counter + the
// "View More" modal (__BVID__1920) with full bet details.
// outerSimpleBar: the reference makes the outer .right-sidebar a SimpleBar root
// only on lucky15, ballbyball, goal and goal2; everywhere else it is a plain div.
// Phones (< 768px) get no SimpleBar at all — the reference renders plain divs there.
const PHONE_MAX_WIDTH = 767
const isPhoneWidth = () => typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH

// Andar Bahar 50 / 150 cards: the reference shows the deal position too
// ("Andar Q/7"). The settlement-facing `selection` stays "Andar Q", the full
// label is stored in player_name.
const POSITION_LABEL_GAMES = new Set(['ab3', 'ab4'])

const SIDEBAR_BET_LIMIT = 10

const CasinoMyBetsSidebar = memo(function CasinoMyBetsSidebar({ myBets = [], gameName = '', extra = null, outerSimpleBar = false, innerRightSidebar = true, formatNation = (n) => n, formatOdds = (bet) => formatStake(bet.odds ?? bet.rate) }) {
  const nationOf = (bet) => {
    if (POSITION_LABEL_GAMES.has(gameName) && bet.player_name) return bet.player_name
    const name = bet.selection || bet.player_name || ''
    // Unique Roulette: "19 to 36 - Half", "10 - Single" like the reference.
    return formatRouletteBetName(gameName, name, bet)
  }
  const [isPhone, setIsPhone] = useState(isPhoneWidth)
  useEffect(() => {
    const onResize = () => setIsPhone(isPhoneWidth())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const [showViewMore, setShowViewMore] = useState(false)
  // Reference: the My Bets card lists only the LATEST 10 bets (View More has the
  // rest). The 10 newest by placement time, kept in the page's own order (Super
  // Over groups by market). Display only — the page's book still uses every bet.
  const shownBets = useMemo(() => {
    if (myBets.length <= SIDEBAR_BET_LIMIT) return myBets
    const time = (b) => new Date(b.created_at || b.createdAt || 0).getTime()
    const latest = new Set([...myBets].sort((a, b) => time(b) - time(a)).slice(0, SIDEBAR_BET_LIMIT))
    return myBets.filter((b) => latest.has(b))
  }, [myBets])
  // Bootstrap fade-in: `show` class ek frame baad lagti hai (animation)
  const [modalEntered, setModalEntered] = useState(false)

  useEffect(() => {
    if (!showViewMore) {
      setModalEntered(false)
      return
    }
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setModalEntered(true)))
    return () => cancelAnimationFrame(raf)
  }, [showViewMore])
  const [allBets, setAllBets] = useState([])
  const [loadingAll, setLoadingAll] = useState(false)
  // Detail hover tooltip (BootstrapVue b-tooltip replica)
  const [tip, setTip] = useState(null) // { text, linkRect }
  const contentRef = useRef(null)
  // `sticky` only after the page scrolls past 12px at width >= 1280 (reference);
  // never on phones, where it would pin My Bets over the table.
  const stickyClass = useStickySidebar()
  const useOuterBar = outerSimpleBar && !isPhone
  // Plain render helpers (not components), so the card is not remounted on every render.
  const wrapOuter = (className, children) => (useOuterBar
    ? <SimpleBarBox className={className} maxHeight={null}>{children}</SimpleBarBox>
    : <div className={className}>{children}</div>)
  // Reference: the Bets counter sits inside the inner .right-sidebar; premium
  // baccarat has no inner .right-sidebar at all.
  const wrapInnerSidebar = (children) => (innerRightSidebar
    ? <div className="right-sidebar">{children}</div>
    : children)
  const wrapInner = (children) => (isPhone
    ? children
    : <SimpleBarBox maxHeight={null} autoHide>{children}</SimpleBarBox>)

  // Mobile "Bets" counter click → My Bets card pe smooth scroll
  const scrollToBets = () => {
    document.getElementById('casino-bets')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const isLayBet = (bet) => String(bet.game_type || bet.type || '').toLowerCase() === 'lay'

  // b-tooltip bottom placement: hovered link ke niche, link pe center-align
  // (coords modal-content relative), arrow left 90px fixed (reference)
  const tipRef = (el) => {
    if (!el || !tip || !contentRef.current) return
    const contentRect = contentRef.current.getBoundingClientRect()
    const w = el.offsetWidth
    const cx = tip.linkRect.left + tip.linkRect.width / 2
    let x = Math.round(cx - contentRect.left - w / 2)
    x = Math.max(8, Math.min(x, contentRect.width - w - 8))
    const y = Math.round(tip.linkRect.bottom - contentRect.top)
    el.style.transform = `translate3d(${x}px, ${y}px, 0px)`
  }

  // View More click → loader overlay (min 1s) ke saath downline ki ACTIVE bets
  // (open-downline, same as the reference: no open bets = "No records found").
  // Pehle all-downline (settled history, LIMIT 100) tha, isliye bahut rows aati thi.
  const handleViewMore = (e) => {
    e?.preventDefault()
    if (loadingAll) return
    setLoadingAll(true)
    Promise.all([
      getCasinoOpenBets(gameName)
        .then((res) => (res?.success && Array.isArray(res?.data) ? res.data : []))
        .catch(() => []),
      new Promise((r) => setTimeout(r, 1000)),
    ])
      .then(([list]) => {
        // open-downline is newest-first (feeds the My Bets card); the
        // reference View More numbers them oldest-first (No 1 = first placed).
        const time = (b) => new Date(b.created_at || b.createdAt || 0).getTime()
        setAllBets([...list].sort((a, b) => time(a) - time(b)))
        setShowViewMore(true)
      })
      .finally(() => setLoadingAll(false))
  }

  return (
    wrapOuter(`right-sidebar${stickyClass}`, <>
      {wrapInnerSidebar(<>
      {/* Reference (desktop): [outer SimpleBar on 4 tables] > right-sidebar > auto-hide SimpleBar > card. */}
      {wrapInner(
      <div className="casino-right-sidebar">
                    <div id="casino-bets" className="card m-b-10 my-bet">
                      <div className="card-header">
                        <h6 className="card-title float-left">My Bets</h6>
                        {' '}
                        <a href="#" className="btn btn-back float-right" onClick={handleViewMore}>View More</a>
                      </div>
                      {' '}
                      <div className="card-body1">
                        <div className="tab-content">
                          <div id="matched-bet" className="tab-pane active">
                            <div className="table-responsive1">
                              <table className="table coupon-table table-striped mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ minWidth: '90px' }}>UserName</th>
                                    <th className="text-right" style={{ minWidth: '50px' }}>Rate</th>
                                    <th className="text-right" style={{ minWidth: '70px' }}>Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {shownBets.length === 0 ? (
                                    <tr>
                                      <td colSpan="4" className="text-center">No records found</td>
                                    </tr>
                                  ) : (
                                    shownBets.map((bet, i) => {
                                      const border = isLayBet(bet) ? 'lay-border' : 'back-border'
                                      return (
                                        <Fragment key={bet.id || bet._id || i}>
                                          <tr className={border}>
                                            <td colSpan="4">
                                              <b>{formatNation(nationOf(bet), bet)}</b>
                                              {' '}
                                              <span className="float-right">{formatPlaceDate(bet.created_at || bet.createdAt)}</span>
                                            </td>
                                          </tr>
                                          <tr className={border}>
                                            <td>{bet.username || bet.user_name || bet.user_id || ''}</td>
                                            <td className="text-right">{formatOdds(bet)}</td>
                                            <td className="text-right">{formatBetAmount(bet.amount ?? bet.stake)}</td>
                                          </tr>
                                          <tr>
                                            <td colSpan="4" style={{ height: '3px', padding: '0px' }}></td>
                                          </tr>
                                        </Fragment>
                                      )
                                    })
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
      {extra}
      </div>
      )}
      {' '}
      <div className="market-show-icon d-none-desktop" onClick={scrollToBets}>
        {/* Counts what the card lists (latest 10), not every bet in the round */}
        <div className="bet-cnt">{Math.min(shownBets.length, 10)}</div>
        {' '}
        <div className="bet-title">Bets</div>
      </div>
      </>)}
      {' '}
      {loadingAll && createPortal(<LoaderOverlay />, document.body)}
      {showViewMore && createPortal(
        <>
          <div className={`modal-backdrop fade${modalEntered ? ' show' : ''}`}></div>
          <div
            id="__BVID__1920"
            role="dialog"
            aria-labelledby="__BVID__1920___BV_modal_title_"
            aria-describedby="__BVID__1920___BV_modal_body_"
            className={`modal fade${modalEntered ? ' show' : ''}`}
            aria-modal="true"
            style={{ display: 'block' }}
            onClick={() => setShowViewMore(false)}
          >
            <div className="modal-dialog modal-big" onClick={(e) => e.stopPropagation()}>
              <span tabIndex="0"></span>
              <div id="__BVID__1920___BV_modal_content_" tabIndex="-1" className="modal-content" ref={contentRef}>
                <header id="__BVID__1920___BV_modal_header_" className="modal-header">
                  <h5 id="__BVID__1920___BV_modal_title_" className="modal-title">View More</h5>
                  <button type="button" aria-label="Close" className="close" onClick={() => setShowViewMore(false)}>
                    &times;
                  </button>
                </header>
                <div id="__BVID__1920___BV_modal_body_" className="modal-body">
                  <div className="m-t-20 view-more-modal">
                    <div id="matched-bet2" className="tab-pane active">
                      <div className="table-responsive">
                        <table className="table table-diamond table-bordered">
                          <thead>
                            <tr>
                              <th>No</th>
                              <th>UserName</th>
                              <th>Nation</th>
                              <th className="text-right">Amount</th>
                              <th className="text-right">User Rate</th>
                              <th>Place Date</th>
                              <th>IP</th>
                              <th>Browser Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allBets.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="text-center">No records found</td>
                              </tr>
                            ) : (
                              allBets.map((bet, i) => (
                                <tr key={bet.id || bet._id || i} className={String(bet.game_type || bet.type || '').toLowerCase() === 'lay' ? 'lay-border' : 'back-border'}>
                                  <td>{i + 1}</td>
                                  <td><span>{bet.username || bet.user_name || bet.user_id || ''}</span></td>
                                  <td>{formatNation(nationOf(bet), bet)}</td>
                                  <td className="text-right">{formatBetAmount(bet.amount ?? bet.stake)}</td>
                                  <td className="text-right">{formatOdds(bet)}</td>
                                  <td>{formatPlaceDate(bet.created_at || bet.createdAt)}</td>
                                  <td>{bet.ip_address || ''}</td>
                                  <td>
                                    <a
                                      href="#"
                                      className="text-success"
                                      onClick={(e) => e.preventDefault()}
                                      {...(tip ? { 'aria-describedby': '__bv_tooltip_1535__' } : {})}
                                      onMouseEnter={(e) => setTip({ text: bet.browser || 'No details available', linkRect: e.currentTarget.getBoundingClientRect() })}
                                      onMouseLeave={() => setTip(null)}
                                    >Detail</a>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                {tip && (
                  <div
                    id="__bv_tooltip_1535__"
                    role="tooltip"
                    tabIndex="-1"
                    ref={tipRef}
                    className="tooltip b-tooltip bs-tooltip-bottom show"
                    x-placement="bottom"
                    style={{ position: 'absolute', willChange: 'transform', top: '0px', left: '0px' }}
                  >
                    <div className="arrow" style={{ left: '90px' }}></div>
                    <div className="tooltip-inner">{tip.text}</div>
                  </div>
                )}
              </div>
              <span tabIndex="0"></span>
            </div>
          </div>
        </>,
        document.body
      )}
    </>)
  )
})

export default CasinoMyBetsSidebar
